import React from 'react';
import { FaCheckCircle, FaBullseye, FaEye, FaAward, FaTrophy, FaSeedling, FaLeaf, FaHome } from 'react-icons/fa';

const About = () => {
  const recognitionPoints = [
    'Strict quality control',
    'Consistent manufacturing standards',
    'Reliable performance in field conditions',
    'Compliance with national standards'
  ];

  const whyChoose = [
    'Over 25 years of irrigation industry experience',
    'Manufacturer and supplier of irrigation systems',
    'BIS recognized quality standards',
    'Durable and field-tested products',
    'Reliable technical support'
  ];

  const industries = [
    { icon: <FaSeedling className="text-4xl" />, name: 'Agriculture' },
    { icon: <FaLeaf className="text-4xl" />, name: 'Landscaping & Horticulture' },
    { icon: <FaHome className="text-4xl" />, name: 'Residential Gardens' },
    { icon: <FaSeedling className="text-4xl" />, name: 'Commercial Green Spaces' }
  ];

  const smartIrrigationBenefits = [
    'Monitor irrigation systems in real time',
    'Automate irrigation schedules',
    'Reduce water wastage',
    'Improve crop yield and quality'
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-[#2E368F] text-white py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">About Ganga Irrigation</h1>
          <p className="text-xl text-white max-w-3xl mx-auto leading-relaxed">
            Over 25 years of excellence in irrigation solutions, supporting farmers and growers with reliable water management systems
          </p>
        </div>
      </section>

      {/* Founder's Message Section */}
      <section className="py-20 bg-white" data-testid="founder-message">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2E368F] mb-4">Founder's Message</h2>
            <div className="w-24 h-1 bg-[#6ABD45] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Founder's Image */}
            <div className="relative">
              <div className="overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="/founder-new.jpg"
                  alt="Founder of Ganga Irrigation"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#6ABD45] text-white px-6 py-3 rounded-lg shadow-lg">
                <p className="font-semibold">— Founder</p>
              </div>
            </div>

            {/* Founder's Message */}
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                At Ganga Irrigation, our journey began with a simple yet powerful vision — to contribute towards efficient water management and sustainable agricultural growth.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With decades of experience in irrigation systems, piping solutions, and water distribution technologies, we have continuously worked towards delivering reliable, high-quality products that empower farmers, industries, and communities. Our commitment is rooted in innovation, durability, and performance, ensuring that every solution we provide adds real value to our customers.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Water is one of the most precious resources, and managing it responsibly is the need of the hour. Through advanced irrigation systems such as drip and sprinkler solutions, we aim to promote water conservation while enhancing productivity across agricultural landscapes.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                At the heart of our organization lies a strong belief in quality, integrity, and customer satisfaction. We continuously upgrade our processes and technologies to meet global standards and evolving market needs, ensuring that our clients receive nothing but the best.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                As we move forward, our focus remains on building long-term relationships, supporting sustainable practices, and contributing to a greener and more efficient future.
              </p>
              <div className="bg-[#6ABD45]/10 border-l-4 border-[#6ABD45] p-6 rounded-r-lg mt-8">
                <p className="text-xl text-[#2E368F] font-semibold italic">
                  "Together, let's grow with innovation and sustainability."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Founded in <strong>1997</strong>, Ganga Irrigation began its journey as a dealership supplying irrigation systems to farmers and agricultural professionals. With years of field experience and a strong understanding of irrigation requirements, the company expanded into manufacturing in <strong>2016</strong>.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Today, Ganga Irrigation manufactures and supplies high-quality drip irrigation and sprinkler systems, providing efficient water management solutions for agriculture, landscaping, and residential applications.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                With over two decades of experience in irrigation, our focus remains on delivering durable, efficient, and accessible irrigation systems that help farmers and growers maximize productivity while conserving water.
              </p>
              <div className="bg-[#6ABD45] bg-opacity-10 border-l-4 border-[#6ABD45] p-6 rounded-r-lg mt-8">
                <p className="text-lg text-gray-800 font-semibold">
                  Our commitment to quality has been recognized by the Bureau of Indian Standards (BIS) with a Certificate of Excellence for achieving <strong>Zero Product Failures</strong> in the last two years.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Recognition */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#6ABD45] rounded-full mb-6">
              <FaTrophy className="text-4xl text-white" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-[#2E368F]">Quality Recognition</h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-10 rounded-lg shadow-sm border border-gray-200">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Ganga Irrigation has been recognized by the <strong>Bureau of Indian Standards (BIS)</strong> with a <strong>Certificate of Excellence</strong> on World Standards Day 2025 for achieving:
              </p>
              
              <div className="bg-[#2E368F] bg-opacity-5 p-8 rounded-lg mb-8 text-center">
                <h3 className="text-2xl font-bold text-[#2E368F] mb-2">Zero Product Failures</h3>
                <p className="text-lg text-gray-700">in the last 2 years</p>
                <p className="text-gray-600 mt-4">Demonstrating outstanding commitment to quality and compliance</p>
              </div>

              <p className="text-lg text-gray-700 mb-4 font-semibold">This recognition reflects our focus on:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recognitionPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-gray-700 mt-8 italic">
                Our adherence to quality benchmarks ensures long-term trust and dependability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Aim & Vision */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Our Aim */}
            <div className="bg-gray-50 p-10 rounded-lg border border-gray-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#6ABD45] rounded-full flex items-center justify-center">
                  <FaBullseye className="text-3xl text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#2E368F]">Our Aim</h2>
              </div>
              <p className="text-lg text-gray-700 mb-6">
                Our aim is to manufacture irrigation systems that are:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                  <span className="text-gray-700">High quality</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Precision engineered</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Accessible and affordable</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 italic leading-relaxed">
                We strongly believe farmers are the backbone of our country, and we are committed to supporting them with reliable water management solutions that enhance productivity and conserve resources.
              </p>
            </div>

            {/* Our Vision */}
            <div className="bg-gray-50 p-10 rounded-lg border border-gray-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#2E368F] rounded-full flex items-center justify-center">
                  <FaEye className="text-3xl text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#2E368F]">Our Vision</h2>
              </div>
              <p className="text-lg text-gray-700 mb-6">
                To become a trusted irrigation solutions provider known for:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Consistent and dependable quality</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Innovative irrigation technologies</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Sustainable water management practices</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Expanding accessibility across agricultural and landscape sectors</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 italic leading-relaxed">
                We aim to continuously evolve by integrating smarter technologies while maintaining strict quality standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-32 bg-[#2E368F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-white">Why Choose Ganga Irrigation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyChoose.map((item, index) => (
              <div key={index} className="flex items-start gap-3 bg-white/10 p-6 rounded-lg backdrop-blur-sm">
                <FaCheckCircle className="text-[#6ABD45] text-xl flex-shrink-0 mt-1" />
                <p className="text-lg text-white">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xl mt-12 text-white max-w-3xl mx-auto leading-relaxed">
            Our systems are designed to deliver consistent performance, water efficiency, and long-term reliability.
          </p>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-[#2E368F]">Industries We Serve</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <div key={index} className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200 hover:border-[#6ABD45] transition-all duration-300">
                <div className="text-[#6ABD45] mb-4 flex justify-center">
                  {industry.icon}
                </div>
                <h3 className="font-semibold text-lg text-[#2E368F]">{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation & Smart Irrigation */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-[#2E368F]">Innovation & Smart Irrigation</h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                Modern irrigation is moving towards precision and digital farming solutions.
              </p>
            </div>

            <div className="bg-white p-10 rounded-lg shadow-sm border border-gray-200">
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Smart irrigation systems allow farmers to monitor and control irrigation remotely while optimizing water usage and improving crop performance.
              </p>

              <h3 className="text-2xl font-bold text-[#2E368F] mb-6">Advanced irrigation technologies can help farmers:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {smartIrrigationBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="bg-[#6ABD45] bg-opacity-10 border-l-4 border-[#6ABD45] p-6 rounded-r-lg">
                <p className="text-lg text-gray-800 leading-relaxed">
                  Ganga Irrigation aims to gradually integrate innovative irrigation technologies and smart water management systems to support modern farming practices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
